import React from 'react';
import TiptapEditor from './TiptapEditor';

interface DescriptionEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  description: string;
  onDescriptionChange: (value: string) => void;
  isSaving: boolean;
  hasChanges: () => boolean;
  error: string | null;
  successMessage: string | null;
}

const DescriptionEditDialog: React.FC<DescriptionEditDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  description,
  onDescriptionChange,
  isSaving,
  hasChanges,
  error,
  successMessage
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-[700px] shadow-lg rounded-md bg-white">
        <div className="mt-3 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Chỉnh sửa mô tả sách</h3>
          
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
              <div className="flex items-start">
                <svg className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <span className="font-medium">Lỗi:</span>
                  <span className="ml-1">{error}</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Success Message */}
          {successMessage && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-md text-sm">
              <div className="flex items-start">
                <svg className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <span className="font-medium">Thành công:</span>
                  <span className="ml-1">{successMessage}</span>
                </div>
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            {/* Description Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mô tả sách</label>
              <TiptapEditor
                value={description}
                onChange={onDescriptionChange}
                placeholder="Nhập mô tả sách..."
                height="400px"
                showToolbar={true}
              />
              <p className="text-xs text-gray-500 mt-1">Hỗ trợ định dạng văn bản phong phú</p>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSaving}
            >
              Hủy
            </button>
            <button
              onClick={onSave}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              disabled={isSaving || !hasChanges()}
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Đang lưu...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Lưu mô tả sách
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DescriptionEditDialog;
